import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { CategoryHue } from '@recipes/contracts';
import { hueFromName } from '@recipes/domain';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@recipes/ui';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { HuePicker } from './hue';

export interface ColourableTag {
  id: string;
  name: string;
  color?: number | null;
}

/**
 * Give tags one of the ten hues (docs/partial/appearance-and-themes-spec.md 6.4, P10). Opened
 * from a recipe: its tags are what the user is looking at. Saved per tap; the colour belongs to
 * the tag, so every recipe that carries it follows.
 */
export function TagColorsDialog({
  open,
  onOpenChange,
  tags,
  onChanged,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tags: ColourableTag[];
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: ({ id, color }: { id: string; color: CategoryHue | null }) =>
      api.taxonomy.updateTag(id, { color }),
    onSuccess: () => {
      onChanged();
      void queryClient.invalidateQueries({ queryKey: ['recipes'] });
    },
    onError: (error) => toastError(error),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')} data-testid="tag-colors-dialog">
        <DialogHeader>
          <DialogTitle>{t('appearance.tagColors.title')}</DialogTitle>
          <DialogDescription>{t('appearance.tagColors.description')}</DialogDescription>
        </DialogHeader>
        <ul className="space-y-4">
          {tags.map((tag) => (
            <li key={tag.id} data-testid="tag-color-row">
              <p className="mb-1 text-sm font-medium">#{tag.name}</p>
              <HuePicker
                label={t('appearance.tagColors.pick', { name: tag.name })}
                value={tag.color}
                automatic={hueFromName(tag.name)}
                disabled={save.isPending}
                onChange={(color) => save.mutate({ id: tag.id, color })}
              />
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button type="button" onClick={() => onOpenChange(false)}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
